import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';
import { PageMode, screenFromUrl } from './where-am-i.tool';

export const NO_PROJECT_REFUSAL = 'No project is open in Zavo. Call open_project with the project\'s name or id, or ask the builder which one to open.';

// Was (2026-10-30), when the screens were three separate lists. SCREENS now holds each screen's label, id need and panels together:
// /** The screens the navigate tool knows about. */
// export const KNOWN_SCREENS = ['home', 'pages', 'page-editor', 'entity', 'workspaces'] as const;
// export type KnownScreen = typeof KNOWN_SCREENS[number];
//
// /** Screens that require an id parameter. */
// const NEEDS_ID: ReadonlySet<KnownScreen> = new Set(['page-editor', 'entity']);
//
// /** Human-readable label for each screen (used in the "Navigated to …" message). */
// const SCREEN_LABEL: Record<KnownScreen, string> = {
//   home: 'Home',
//   pages: 'Pages',
//   'page-editor': 'Page editor',
//   entity: 'Data (entity)',
//   workspaces: 'Workspaces',
// };

interface ScreenInfo {
  /** Used in the "Navigated to …" message. */
  label: string;
  needsId?: boolean;
  /** Builder-level screens and the project picker work before any project is open. */
  worksWithoutProject?: boolean;
  /** The tabs and panels the screen can be asked to show. */
  panels?: readonly string[];
}

// Modules and AI Query are hidden from builders and the OOTB screen is internal, so none of them is here.
const SCREENS = {
  'select-project': { label: 'Select project', worksWithoutProject: true, panels: ['create-project'] },
  data: { label: 'Data' },
  entity: { label: 'Data (entity)', needsId: true, panels: ['predefined-rows', 'role-permissions'] },
  pages: { label: 'Pages', panels: ['all', 'web', 'mobile', 'pdf', 'new-page'] },
  'page-preview': { label: 'Page in Preview', needsId: true },
  'page-spotlight': { label: 'Page in Spotlight', needsId: true },
  'page-data': { label: 'Page data', needsId: true, panels: ['add-variable'] },
  'function-editor': { label: 'Function editor', needsId: true },
  team: { label: 'Team', panels: ['add-member'] },
  workspaces: { label: 'Workspaces', panels: ['workspaces', 'roles'] },
  bastion: { label: 'Bastion' },
  // Was (2026-10-30): schema: { label: 'Schema explorer', panels: ['tables', 'relationships'] },
  // Claude doesn't reach this screen. where_am_i still names it, as it does Modules.
  'chart-style': { label: 'Chart style', worksWithoutProject: true },
  settings: {
    label: 'Settings',
    panels: ['builder-code', 'data-tenant', 'mobile-app', 'iphone-app', 'phone-bars', 'works-offline', 'time-zone', 'preview', 'publish', 'live-login'],
  },
} as const satisfies Record<string, ScreenInfo>;

export type KnownScreen = keyof typeof SCREENS;
export const KNOWN_SCREENS = Object.keys(SCREENS) as KnownScreen[];

/** Screens that track unsaved edits — only these are checked before leaving. */
// Was (2026-10-30), when Team's add form and the picker's new project were dropped silently: new Set(['page-editor', 'entity', 'pages', 'page-data', 'workspaces', 'chart-style']);
const HAS_UNSAVED: ReadonlySet<string> = new Set(['page-editor', 'entity', 'pages', 'page-data', 'workspaces', 'chart-style', 'team', 'select-project']);

/**
 * Dependencies the navigate tool needs from the editor at runtime.
 * The spec provides stubs; the real wiring injects Angular services.
 */
export interface NavigateDeps {
  /** Open a screen, optionally by id. False for an unknown id; `refused` when the screen gives its own reason. */
  go: (screen: KnownScreen, id?: string) => Promise<boolean | { refused: string }>;
  /** Return unsaved-edit state for the screen the builder is currently on, or null if clean / not applicable. */
  unsaved: () => { count: number; name: string } | null;
  /** The id of the page or entity open on the builder's screen, or null when none is. */
  openHere: () => string | null;
  /** Which mode the page editor is in, when it is open. */
  pageMode?: () => PageMode | null;
  settingsOpen?: () => boolean;
  /** A form in Settings that closing it would lose; Settings opens over any screen, so this is asked wherever the route is. */
  settingsUnsaved?: () => { count: number; name: string } | null;
  closeSettings?: () => void;
  /** Show a tab or panel of the screen just opened. False when it did not show. */
  selectPanel?: (screen: KnownScreen, panel: string) => Promise<boolean>;
  /** The screen's own reason a panel isn't on offer, when it knows one. */
  whyPanelNotShown?: (screen: KnownScreen, panel: string) => string | null;
}

function settingsRefusal(deps: Pick<NavigateDeps, 'settingsUnsaved'>): ToolAnswer | null {
  const dirty = deps.settingsUnsaved?.();
  return dirty ? { outcome: 'refused', refusal: `${dirty.name} has unsaved changes; ask the builder to save or discard them first.` } : null;
}

/** The refusal for work that leaving this screen would lose, or null when there is none. */
export function unsavedRefusal(ctx: ToolContext, deps: Pick<NavigateDeps, 'unsaved' | 'settingsUnsaved'>): ToolAnswer | null {
  const inSettings = settingsRefusal(deps);
  if (inSettings) return inSettings;
  if (!HAS_UNSAVED.has(screenFromUrl(ctx.routerUrl))) return null;
  const dirty = deps.unsaved();
  if (!dirty) return null;
  return {
    outcome: 'refused',
    refusal: `${dirty.name} has unsaved changes; ask the builder to save or discard them first.`,
  };
}

function isOpenHere(screen: KnownScreen, id: string | undefined, ctx: ToolContext, deps: NavigateDeps): boolean {
  const route = screenFromUrl(ctx.routerUrl);
  if (screen === 'settings') return !!deps.settingsOpen?.();
  if (!id || deps.openHere() !== id) return false;
  if (screen === 'page-preview') return route === 'page-editor' && (deps.pageMode?.() ?? 'preview') === 'preview';
  if (screen === 'page-spotlight') return route === 'page-editor' && deps.pageMode?.() === 'spotlight';
  return route === screen;
}

function panelProblem(screen: KnownScreen, panel: string): string | null {
  const info: ScreenInfo = SCREENS[screen];
  if (!info.panels) return `${info.label} has no tabs or panels to open.`;
  if (info.panels.includes(panel)) return null;
  return `${info.label} has no tab or panel '${panel}'. It has: ${info.panels.join(', ')}.`;
}

/**
 * navigateFirst — reusable helper for tools that need to reach a screen before acting.
 *
 * 1. Does nothing when the target is already open.
 * 2. Checks for unsaved edits on the current screen (if the screen tracks them).
 * 3. If unsaved edits exist, returns a refusal.
 * 4. Otherwise navigates and returns success or a refusal if the target is invalid.
 */
export async function navigateFirst(
  screen: string,
  id: string | undefined,
  ctx: ToolContext,
  deps: NavigateDeps,
  panel?: string,
): Promise<ToolAnswer> {
  if (!KNOWN_SCREENS.includes(screen as KnownScreen)) {
    return {
      outcome: 'refused',
      refusal: `Unknown screen '${screen}'. Known screens: ${KNOWN_SCREENS.join(', ')}.`,
    };
  }

  const typed = screen as KnownScreen;
  const info: ScreenInfo = SCREENS[typed];

  if (!ctx.projectId && !info.worksWithoutProject) return { outcome: 'refused', refusal: NO_PROJECT_REFUSAL };

  if (info.needsId && !id) {
    return {
      outcome: 'refused',
      refusal: `Screen '${typed}' needs an id. Use read_project to find it.`,
    };
  }

  if (panel) {
    const problem = panelProblem(typed, panel);
    if (problem) return { outcome: 'refused', refusal: problem };
  }

  // Was (2026-10-30), before Settings, panels and a screen's own refusal; unsavedRefusal and the checks below replaced it:
  //   const currentScreen = screenFromUrl(ctx.routerUrl);
  //   // Already there: nothing is left, so the builder's unsaved edits stay and there is nothing to ask.
  //   if (id && currentScreen === typed && deps.openHere() === id) {
  //     return { outcome: 'done', changed: [], result: { alreadyOpen: true } };
  //   }
  //
  //   // Check for unsaved edits on the current screen (only if the current screen tracks them).
  //   if (HAS_UNSAVED.has(currentScreen)) {
  //     const dirty = deps.unsaved();
  //     if (dirty) {
  //       return {
  //         outcome: 'refused',
  //         refusal: `${dirty.name} has unsaved changes; ask the builder to save or discard them first.`,
  //       };
  //     }
  //   }
  //
  //   const success = await deps.go(typed, id);
  //   if (!success) {
  //     return {
  //       outcome: 'refused',
  //       refusal: `Could not open ${SCREEN_LABEL[typed]}${id ? ` '${id}'` : ''}. Check the id with read_project.`,
  //     };
  //   }
  //
  //   return {
  //     outcome: 'done',
  //     changed: [`Navigated to ${SCREEN_LABEL[typed]}`],
  //   };

  const changed: string[] = [];
  // Already there: nothing is left, so the builder's unsaved edits stay and there is nothing to ask.
  const alreadyOpen = isOpenHere(typed, id, ctx, deps);

  if (typed !== 'settings') {
    const inSettings = settingsRefusal(deps);
    if (inSettings) return inSettings;
  }

  if (!alreadyOpen) {
    // Settings opens over the entity page, so reaching it there loses nothing; every other way of reaching it leaves.
    const leaves = !(typed === 'settings' && screenFromUrl(ctx.routerUrl) === 'entity');
    const refusal = leaves ? unsavedRefusal(ctx, deps) : null;
    if (refusal) return refusal;
  }

  // The overlay outlives a route change and would still cover the screen the builder was sent to.
  if (typed !== 'settings' && deps.settingsOpen?.()) {
    deps.closeSettings?.();
    if (alreadyOpen) changed.push('Closed Settings');
  }

  if (!alreadyOpen) {
    const went = await deps.go(typed, id);
    if (typeof went === 'object') return { outcome: 'refused', refusal: went.refused };
    if (!went) {
      return {
        outcome: 'refused',
        refusal: `Could not open ${info.label}${id ? ` '${id}'` : ''}. Check the id with read_project.`,
      };
    }
    changed.push(`Navigated to ${info.label}`);
  }

  const result: Record<string, unknown> = {};
  if (alreadyOpen) result['alreadyOpen'] = true;
  if (panel) {
    if (await deps.selectPanel?.(typed, panel)) changed.push(`Opened ${panel}`);
    else {
      const why = deps.whyPanelNotShown?.(typed, panel);
      result['panelNotShown'] = why
        ? `${panel} did not show on ${info.label}: ${why} Say so rather than assuming it is open.`
        : `${panel} did not show on ${info.label}. The builder's access may not include it; say so rather than assuming it is open.`;
    }
  }

  return { outcome: 'done', changed, ...(Object.keys(result).length ? { result } : {}) };
}

export function makeNavigateTool(deps: NavigateDeps): ToolDef {
  const panelLines = KNOWN_SCREENS
    .map((s): [string, readonly string[] | undefined] => [s, (SCREENS[s] as ScreenInfo).panels])
    .filter(([, panels]) => panels)
    .map(([s, panels]) => `${s}: ${panels!.join(', ')}`)
    .join('; ');
  return {
    name: 'navigate',
    kind: 'live',
    worksWithoutProject: true,
    description: [
      `Moves the builder's screen. Screens: ${KNOWN_SCREENS.join(', ')}.`,
      'entity, page-preview, page-spotlight, page-data and function-editor need the id from read_project.',
      'page-preview shows the page as it runs; page-spotlight opens it to change with Spotlight. They are separate: pick the one the builder means.',
      'workspaces takes an optional workspace id to open that workspace, and bastion an optional flow id.',
      `panel selects a tab or panel on arrival. ${panelLines}.`,
      'Function editor and Bastion are for arriving only; no other tool works inside them.',
      'select-project and chart-style work with no project open; every other screen needs one (open_project).',
      'Call it before show, which only finds things on the current screen.',
      'It won\'t leave a screen with unsaved changes; ask the builder to save or discard them first.',
      'Returns what it opened.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        screen: {
          type: 'string',
          enum: [...KNOWN_SCREENS],
          description: 'The screen to navigate to.',
        },
        id: {
          type: 'string',
          description: 'Entity, page, workspace or Bastion flow id, where the screen takes one.',
        },
        panel: {
          type: 'string',
          description: 'A tab or panel of that screen to show.',
        },
      },
      required: ['screen'],
      additionalProperties: false,
    },

    async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const screen = input['screen'] as string;
      const id = input['id'] as string | undefined;
      const panel = input['panel'] as string | undefined;
      return navigateFirst(screen, id, ctx, deps, panel);
    },
  };
}
